<script setup lang="ts">
import { computed, ref, type HTMLAttributes } from 'vue'

import { handleCatchedError } from '@/helpers/common'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useDocumentsStore } from '@/store/documents'

import { DSButton, DSPopover, DSPopoverContent, DSPopoverTrigger, DSSkeleton } from '@ds'
import { cn } from '@ds/lib/utils'
import { ImageIcon } from '@lucide/vue'

defineOptions({ inheritAttrs: false })

type Props = {
  /** Asset id — used only to look up a thumbnail to preview on hover. Display text is `name`. */
  id: string
  name: string
  class?: HTMLAttributes['class']
}

const props = defineProps<Props>()

const emit = defineEmits<{ click: [MouseEvent] }>()

const assetsBunchStore = useAssetsBunchStore()
const documentsStore = useDocumentsStore()

const imageSrc = ref<string>()
const isLoadingThumbnail = ref(false)

const assetInstance = computed(() => assetsBunchStore.getElementById(props.id))
const hasThumbnail = computed(() => !!assetInstance.value?.thumbnailId)

const loadImage = (src: string) =>
  new Promise<void>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve()
    image.onerror = () => reject(new Error(`Failed to load asset thumbnail: ${src}`))
    image.src = src
  })

const fetchThumbnail = async () => {
  const thumbnailId = assetInstance.value?.thumbnailId
  if (!thumbnailId) return
  if (assetInstance.value?.thumbnailUrl) {
    imageSrc.value = assetInstance.value.thumbnailUrl
    return
  }
  try {
    isLoadingThumbnail.value = true
    const document = await documentsStore.fetchSingle(thumbnailId)
    if (!document.url) return
    await loadImage(document.url)
    if (assetInstance.value) assetInstance.value.thumbnailUrl = document.url
    imageSrc.value = document.url
  } catch (e) {
    handleCatchedError(e as string)
  } finally {
    isLoadingThumbnail.value = false
  }
}
</script>

<template>
  <span class="flex items-center gap-1">
    <span
      :title="name"
      :class="cn('block min-w-0 truncate hover:underline underline-offset-2 decoration-foreground/25', props.class)"
      @click="emit('click', $event)"
    ><slot>{{ name }}</slot></span>
    <DSPopover v-if="hasThumbnail">
      <DSPopoverTrigger
        as-child
        @click.stop="fetchThumbnail"
      >
        <DSButton
          type="button"
          variant="ghost"
          size="icon-sm"
          class="size-5.5 shrink-0 text-foreground/35 hover:bg-foreground/10 hover:text-foreground"
        >
          <ImageIcon aria-hidden="true" />
        </DSButton>
      </DSPopoverTrigger>
      <DSPopoverContent
        align="start"
        class="w-48 aspect-video p-0 overflow-hidden"
      >
        <img
          v-if="imageSrc"
          :src="imageSrc"
          :alt="name"
          class="w-full h-full object-cover"
        >
        <DSSkeleton
          class="w-full h-full"
        />
      </DSPopoverContent>
    </DSPopover>
  </span>
</template>
